/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {BulkActionEntity, GenericObject} from '@ion/core/src/types/entities';
import {FormState, useFormState} from '@ion/dls/src/hooks/useFormState';
import {useLoadSCIDevices} from '@ion/query/src/hooks/useLoadSCIDevices';
import useCurrentGlobalGroupId from '../..//hooks/useCurrentGlobalGroupId';
import {SCIUpdateProps} from '../../bulk/useSCIUpdate';
import {getAllSCIDevices} from '../../pages/devices/SCISettingsUtils';

export interface UseSCIUpdateSettingsResult {
  setFormState?: (val: FormState) => void;
  formState?: GenericObject;
  setShowSnackBar?: (showSnackBar: boolean) => void;
  showSnackBar?: boolean;
  setErrorMessage?: (errorMessage: boolean) => void;
  errorMessage?: boolean;
  noSCIDevice?: number;
  setUpdatedSCISettings?: (updatedSCISettings: {}) => void;
  updatedSCISettings?: {};
  setBulkSCISetting?: (bulkSCISetting: {}) => void;
  bulkSCISetting?: {};
  activeStep?: number;
  typeOptions?: GetOptionsResult;
  onTypeChange?: ({target}: ChangeEvent<{
    name?: string;
    value?: string;
  }>) => void;
  deviceTypes?: GenericObject;
  entity?: BulkActionEntity;
}

export interface GetOptions {
  value: string | boolean | any;
  label: string | any;
};

export type GetOptionsResult = GetOptions[];

export enum ActiveStep {
  FIRST = 0,
  SECOND = 1
};

/**
 * Re-usable hook for managing the actions of SCI Update container.
 */
export const useSCIUpdateSettings = ({
  setTaskNickNameAction,
  setSelectedSCIDevices,
  setSelectedSCITypes,
  setSelectedDeviceType,
  entitySelectionFormState,
  selectedRowEntities,
  bulkImportParams,
  entity
}: SCIUpdateProps): UseSCIUpdateSettingsResult => {
  const [formState, setFormState]                   = useFormState();
  const [showSnackBar, setShowSnackBar]             = useState<boolean>(false);
  const [errorMessage, setErrorMessage]             = useState<boolean>(false);
  const [noSCIDevice, setNoSCIDevice]               = useState<number>(1);
  const [updatedSCISettings, setUpdatedSCISettings] = useState({});
  const [bulkSCISetting, setBulkSCISetting]         = useState({});
  const [deviceTypes, setDeviceTypes]               = useState<GenericObject>({});
  const [activeStep, setActiveStep]                 = useState<number>(ActiveStep.FIRST);
  const [deviceType, setDeviceType]                 = useState<string>();
  const [idType, setIdType]                         = useState<GenericObject>();
  const accountId                                   = useCurrentGlobalGroupId();
  const path                                        = 'admin-devices:view.bulkSciSetting';
  const {loadSCIDevices}                            = useLoadSCIDevices();

  useEffect(() => {
    if (selectedRowEntities?.length) {
      validateGridSelection(deviceType);
    } else {
      validateUploadSelection(deviceType, idType);
    }
    setErrorMessage(false);
  }, [activeStep]);

  const getOptions = (key: string): GetOptionsResult => {
    const entries = translate(`${path}.${key}`, {returnObjects: true});
    const result  = Object.entries(entries).map(([value, label]) => (
      {value, label}
    ));
    return result;
  };

  const typeOptions = useMemo((): GetOptionsResult => (
    getOptions('typeOptions')
  ), []);

  const onTypeChange = useCallback((
    {target}: ChangeEvent<{name?: string, value?: string}>
  ) => {
    const {name, value} = target;
    setTaskNickNameAction(`(${value})`);
    setDeviceTypes({
      [name] : value,
      idType : entitySelectionFormState?.entityIdentifierType?.value?.value
    });
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setDeviceType(value);
    setIdType(entitySelectionFormState?.entityIdentifierType?.value?.value);
  }, []);

  const validateGridSelection = useCallback((deviceType) => {
    if (deviceType) {
      setSelectedDeviceType(deviceType);
      setBulkSCISetting({});
      setActiveStep(1);
    } else {
      setShowSnackBar(true);
    }
  }, [activeStep, deviceTypes]);

  const getIdsByTextField = useCallback((): string[] => {
    let identifiers = entitySelectionFormState?.entityIdentifiers?.value;
    if (bulkImportParams?.items?.length) {
      identifiers = bulkImportParams?.items?.map((item) => item?.deviceIdentifier);
    }
    return identifiers;
  }, [entitySelectionFormState, bulkImportParams]);

  const validateUploadSelection = useCallback((deviceType, idType) => {
    const identifiers = getIdsByTextField();
    try {
      if (!isEmpty(deviceType)) {
        const SCIDeviceType = {
          deviceType : deviceType,
          idType     : idType,
          hierLevel  : true,
          entity     : entity
        };
        loadSCIDevices({
          accountId,
          identifiers,
          SCIDeviceType
        }, {
          onSuccess : (device) => {
            if (device?.length) {
              const devices = device.map(({device}) => device);
              const {selectedAllTypes, sciDevices} = getAllSCIDevices(devices);
              setSelectedSCIDevices(sciDevices);
              setSelectedDeviceType(deviceType);
              setActiveStep(1);
              setSelectedSCITypes(selectedAllTypes);
            } else {
              setShowSnackBar(true);
              setNoSCIDevice(device?.length);
            }
          },
          onError : () => {
            setShowSnackBar(true);
          }
        });
      } else {
        setShowSnackBar(true);
      }
    } catch (err) {
      setShowSnackBar(true);
    }
  }, [activeStep, deviceTypes, accountId]);

  return {
    setFormState,
    formState,
    setShowSnackBar,
    showSnackBar,
    setErrorMessage,
    errorMessage,
    noSCIDevice,
    setUpdatedSCISettings,
    updatedSCISettings,
    setBulkSCISetting,
    bulkSCISetting,
    activeStep,
    typeOptions,
    onTypeChange,
    deviceTypes
  };
};
